/**
 * Storage Utilities
 * Provides type-safe storage operations with IndexedDB fallback to localStorage
 */
import { get, set, del, clear as clearIdb, keys } from 'idb-keyval';
import { STORAGE_KEYS } from '../constants/storage';

/**
 * Save data to storage
 */
export async function save<T>(key: string, value: T): Promise<void> {
  try {
    await set(key, value);
  } catch (error) {
    // Fallback to localStorage if IndexedDB fails
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (localError) {
      console.error('Failed to save to storage:', localError);
      throw localError;
    }
  }
}

/**
 * Load data from storage
 */
export async function load<T>(key: string): Promise<T | null> {
  try {
    const value = await get<T>(key);
    if (value !== undefined) {
      return value;
    }
  } catch (error) {
    // Fallback to localStorage
    try {
      const item = localStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
    } catch (localError) {
      console.error('Failed to load from storage:', localError);
    }
  }
  return null;
}

/**
 * Remove data from storage
 */
export async function remove(key: string): Promise<void> {
  try {
    await del(key);
  } catch (error) {
    // Fallback to localStorage
    try {
      localStorage.removeItem(key);
    } catch (localError) {
      console.error('Failed to remove from storage:', localError);
    }
  }
}

/**
 * Clear all data from storage
 */
export async function clear(): Promise<void> {
  try {
    await clearIdb();
  } catch (error) {
    // Fallback to localStorage
    try {
      localStorage.clear();
    } catch (localError) {
      console.error('Failed to clear storage:', localError);
    }
  }
}

/**
 * Save string to storage
 */
export async function saveString(key: string, value: string): Promise<void> {
  await save(key, value);
}

/**
 * Load string from storage
 */
export async function loadString(key: string): Promise<string | null> {
  return await load<string>(key);
}

/**
 * Get all storage keys
 */
export async function getAllKeys(): Promise<string[]> {
  try {
    return await keys();
  } catch (error) {
    // Fallback to localStorage
    try {
      return Object.keys(localStorage);
    } catch (localError) {
      console.error('Failed to get storage keys:', localError);
      return [];
    }
  }
}

